import {action, observable, computed, runInAction, toJS} from 'mobx';
import {EventEmitter} from 'events';
import filter from 'lodash/filter';
import some from 'lodash/some';
import map from 'lodash/map';
import flatten from 'lodash/flatten';
import forEach from 'lodash/forEach';
import {getRootStore} from '../../../app.bootstrap';
import {ISearchTagsController} from './SearchTags.contracts';
import {requireImage} from '../../../utils/utils';

export enum SearchTagsEvents {
    SEARCH_COMPLETE = 'SEARCH_COMPLETE'
}

interface SearchResult {
    resources: any[];
    roomId: string | null;
}

export class SearchTagsController implements ISearchTagsController {
    @observable public searchTerm: string = '';
    @observable public isSubmitted: boolean = false;
    @observable public hasResults: boolean = false;
    @observable public isEmpty: boolean = false;
    @observable public isDirty: boolean = false;
    @observable public isSearching: boolean = false;

    public readonly translate: (key: string, values?: Record<string, any>) => string;
    public readonly useNewInterface: boolean;
    public readonly requireImage: (imageName: string, useNewInterface: boolean) => string;
    private readonly eventEmitter = new EventEmitter();

    constructor(
        useNewInterface: boolean = false,
        private readonly searchTags: boolean = false,
        private searchHistoryTags: boolean = false,
        private readonly searchLibraryTags: boolean = false,
        private readonly libraryTaggingMode: boolean = false
    ) {
        this.translate = getRootStore().localizationService.translate;
        this.useNewInterface = useNewInterface;
        this.requireImage = requireImage;
    }

    @computed
    get shouldShow(): boolean {
        return this.searchTags && ((this.searchHistoryTags && !this.libraryTaggingMode) || this.searchLibraryTags);
    }

    // Action methods for the interface
    @action
    onSearchTermChange = (term: string): void => {
        this.searchTerm = term;
        this.isEmpty = false;
        this.isSubmitted = false;
        this.isDirty = true;
    };

    @action
    onSubmit = (): void => {
        if (!this.searchTerm || !this.searchTerm.trim()) {
            this.isEmpty = true;
            return;
        }
        this.isSubmitted = true;
        this.isDirty = true;
        this.eventEmitter.emit(SearchTagsEvents.SEARCH_COMPLETE, this.searchTerm);
    };

    /**
     * Search for tagged resources in customer history
     */
    @action
    searchInHistory = (customerHistory: any, searchTerm: string): void => {
        if (!searchTerm || !customerHistory || !customerHistory.records) {
            this.hasResults = false;
            this.eventEmitter.emit(SearchTagsEvents.SEARCH_COMPLETE, []);
            return;
        }

        this.isSearching = true;
        this.searchTerm = searchTerm;

        const results: SearchResult[] = [];

        forEach(toJS(customerHistory.records), (record) => {
            const resources = this.findTaggedResources([record.images, record.videos], searchTerm);
            const roomId = record.roomId ? record.roomId._id : null;

            if (resources.length > 0) {
                results.push({resources, roomId});
            }
        });

        this.isSearching = false;
        this.hasResults = results.length > 0;
        this.eventEmitter.emit(SearchTagsEvents.SEARCH_COMPLETE, results);
    };

    /**
     * Find resources that have tags matching the search term
     */
    findTaggedResources = (resources: any[], searchTerm: string): any[] => {
        const search = new RegExp(searchTerm.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&'), 'i');

        const mapped = map(resources, (resourcesType) => {
            if (!resourcesType || !Array.isArray(resourcesType)) {
                return [];
            }

            return filter(resourcesType, (resource) => {
                const hasTags = resource && resource.tags && Array.isArray(resource.tags);

                if (!hasTags) {
                    return false;
                }

                return some(resource.tags, (tag) => search.test(tag.text));
            });
        });

        return flatten(mapped);
    };

    @action
    clearSearch = (): void => {
        this.searchTerm = '';
        this.isSearching = false;
    };

    // EventEmitter delegation methods
    on(event: string, listener: (...args: any[]) => void): this {
        this.eventEmitter.on(event, listener);
        return this;
    }

    removeAllListeners(event?: string): this {
        this.eventEmitter.removeAllListeners(event);
        return this;
    }

    dispose(): void {
        this.eventEmitter.removeAllListeners();
    }
}
